/**
 * BacklinkArticleCard — Displays an approved Claude.ai article on a scenario detail page
 *
 * Only renders if there's an approved backlink_artifacts entry for the scenario.
 * Acts as a "Featured on Claude.ai" trust signal + outbound link to the article.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, BookOpen } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

type Article = {
  id: string;
  artifact_url: string;
  artifact_title: string | null;
  published_at: string | null;
};

type Props = {
  scenarioId: string | null | undefined;
  className?: string;
};

export function BacklinkArticleCard({ scenarioId, className = "" }: Props) {
  const { data: article } = useQuery({
    queryKey: ["approved-article", scenarioId],
    queryFn: async () => {
      if (!scenarioId) return null;
      const { data, error } = await (supabase as any)
        .from("backlink_artifacts")
        .select("id, artifact_url, artifact_title, published_at")
        .eq("scenario_id", scenarioId)
        .eq("approval_status", "approved")
        .order("reviewed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.warn("[BacklinkArticleCard]", error);
        return null;
      }
      return data as Article | null;
    },
    enabled: !!scenarioId,
    staleTime: 5 * 60 * 1000,
  });

  const { isDark } = useTheme();

  if (!article) return null;

  const bg = isDark ? "rgba(197,165,90,0.06)" : "rgba(0,40,104,0.03)";
  const bgHover = isDark ? "rgba(197,165,90,0.12)" : "rgba(0,40,104,0.06)";
  const border = isDark ? "rgba(197,165,90,0.20)" : "rgba(0,40,104,0.15)";
  const borderHover = isDark ? "rgba(197,165,90,0.40)" : "rgba(0,40,104,0.30)";
  const labelColor = isDark ? "rgba(197,165,90,0.75)" : "rgba(0,40,104,0.65)";
  const titleColor = isDark ? "#f0ebe3" : "#002868";
  const iconColor = isDark ? "rgba(197,165,90,0.60)" : "rgba(0,40,104,0.55)";

  return (
    <a
      href={article.artifact_url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={{
        display: "flex",
        gap: 16,
        padding: "16px 20px",
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        textDecoration: "none",
        color: "inherit",
        transition: "all 0.18s ease",
        cursor: "pointer",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = bgHover;
        e.currentTarget.style.borderColor = borderHover;
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = bg;
        e.currentTarget.style.borderColor = border;
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 8,
          backgroundColor: "rgba(197,165,90,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#C5A55A",
        }}
      >
        <BookOpen size={22} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: labelColor,
            marginBottom: 4,
          }}
        >
          Featured Article on Claude.ai
        </div>
        <div
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 17,
            lineHeight: 1.3,
            color: titleColor,
            fontWeight: 500,
          }}
        >
          {article.artifact_title || "Read the full pricing breakdown"}
        </div>
      </div>
      <div
        style={{
          flexShrink: 0,
          alignSelf: "center",
          color: iconColor,
        }}
      >
        <ExternalLink size={18} />
      </div>
    </a>
  );
}
